import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronUp,
  Circle,
  Clock,
  Loader2,
  Mic,
  MicOff,
  Square,
} from 'lucide-react';

import { AudioLevelBars } from '@/components/demo-studio/AudioLevelBars';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import demoStudioService, { type DemoStudioSession } from '@/lib/api/demoStudio';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAudioDevices } from '@/hooks/useAudioDevices';
import { useAudioLevel } from '@/hooks/useAudioLevel';
import { useAudioRecording } from '@/hooks/useAudioRecording';

const ACTIVE_STATUSES = new Set(['launching', 'active', 'processing']);
const SESSION_POLL_INTERVAL_MS = 15_000;
const launchStorageKey = (sessionId: string) => `demo-studio-launch:${sessionId}`;
const HEADFUL_LIVE_VIEW_RATIO = 16 / 9;
const FULLSCREEN_LIVE_VIEW_RATIO = 1.9;
const FULLSCREEN_LIVE_VIEW_ZOOM = 1.05;

const formatDuration = (totalSeconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
  }

  return [minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
};

const DemoStudioSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { toast } = useToast();
  const [loading, setLoading] = React.useState(true);
  const [stopping, setStopping] = React.useState(false);
  const [stopRequested, setStopRequested] = React.useState(false);
  const [stopRequestedAtMs, setStopRequestedAtMs] = React.useState<number | null>(null);
  const [session, setSession] = React.useState<DemoStudioSession | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = React.useState(0);
  const { micStatus, startRecording, stopRecording } = useAudioRecording();
  const { inputDevices } = useAudioDevices();
  const [selectedDeviceId, setSelectedDeviceId] = React.useState<string | undefined>(undefined);
  const { audioLevel, micConnected, stream: monitorStreamRef } = useAudioLevel(selectedDeviceId);
  const [muted, setMuted] = React.useState(false);

  const toggleMute = React.useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      // Mute/unmute all audio tracks on the monitoring stream
      monitorStreamRef.current?.getAudioTracks().forEach((t) => {
        t.enabled = !next;
      });
      return next;
    });
  }, [monitorStreamRef]);

  // Re-apply mute state when the monitoring stream reconnects (e.g. after device switch)
  React.useEffect(() => {
    if (muted && monitorStreamRef.current) {
      monitorStreamRef.current.getAudioTracks().forEach((t) => {
        t.enabled = false;
      });
    }
  }, [micConnected, muted, monitorStreamRef]);

  const timingMarksRef = React.useRef({ visible: false, narration_start: false });

  React.useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      rootOverflow: root?.style.overflow ?? '',
    };

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    if (root) {
      root.style.overflow = 'hidden';
    }

    return () => {
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      if (root) {
        root.style.overflow = previous.rootOverflow;
      }
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    const loadSession = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await demoStudioService.getSession(sessionId);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to load Studio session.');
        }

        if (!cancelled) {
          setSession(response.data);
        }
      } catch (error) {
        if (!cancelled) {
          toast({
            title: 'Unable to load session',
            description: error instanceof Error ? error.message : 'Failed to load Studio session.',
            variant: 'destructive',
          });
          navigate('/demo-studio', { replace: true });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadSession();

    return () => {
      cancelled = true;
    };
  }, [navigate, sessionId, toast]);

  // Clean up the launch context from sessionStorage once the session page has loaded
  React.useEffect(() => {
    if (sessionId) {
      sessionStorage.removeItem(launchStorageKey(sessionId));
    }
  }, [sessionId]);

  // Poll for session status changes so we detect server-side abandonment.
  // We track the status string separately so the interval doesn't reset on
  // every poll response (which would produce a new session object each time).
  const sessionStatus = session?.status;
  const abandonedAudioFlushedRef = React.useRef(false);
  React.useEffect(() => {
    if (!sessionId || !sessionStatus || !ACTIVE_STATUSES.has(sessionStatus) || stopRequested) return;

    const interval = window.setInterval(async () => {
      try {
        const response = await demoStudioService.getSession(sessionId);
        if (response.success && response.data) {
          setSession(response.data);

          // When the backend marks the session as abandoned (timeout / CDP
          // disconnect), flush any in-progress audio recording and upload it
          // so the narration is not lost.
          if (
            response.data.status === 'abandoned' &&
            !abandonedAudioFlushedRef.current
          ) {
            abandonedAudioFlushedRef.current = true;
            if (micStatus === 'active') {
              try {
                const narrationBlob = await stopRecording();
                if (narrationBlob && narrationBlob.size > 0) {
                  const file = new File([narrationBlob], 'narration.webm', {
                    type: narrationBlob.type || 'audio/webm',
                  });
                  await demoStudioService.uploadAudio(sessionId, file);
                }
              } catch {
                // Best-effort — the backend will still recover the video without audio
              }
            }
          }
        }
      } catch {
        // Swallow polling errors silently
      }
    }, SESSION_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [sessionId, sessionStatus, stopRequested, micStatus, stopRecording]);

  React.useEffect(() => {
    timingMarksRef.current = {
      visible: Boolean(session?.visible_start_ms != null),
      narration_start: Boolean(session?.narration_start_ms != null),
    };
  }, [session?.id, session?.visible_start_ms, session?.narration_start_ms]);

  React.useEffect(() => {
    if (!session?.created_at || session?.narration_start_ms == null) {
      setElapsedSeconds(0);
      return;
    }

    const startMs = new Date(session.created_at).getTime() + session.narration_start_ms;
    const endMs = session.completed_at
      ? new Date(session.completed_at).getTime()
      : stopRequestedAtMs;

    const updateElapsed = () => {
      const referenceMs = endMs ?? Date.now();
      setElapsedSeconds(Math.max(0, (referenceMs - startMs) / 1000));
    };

    updateElapsed();

    if (endMs) {
      return;
    }

    const interval = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(interval);
  }, [session?.created_at, session?.completed_at, session?.narration_start_ms, stopRequestedAtMs]);

  const markTimingEvent = React.useCallback(
    async (eventType: 'visible' | 'narration_start') => {
      if (!session?.id || timingMarksRef.current[eventType]) {
        return;
      }

      const response = await demoStudioService.markTimingEvent(session.id, eventType);
      if (!response.success || !response.data) {
        throw new Error(response.error || `Failed to mark ${eventType} timing.`);
      }

      timingMarksRef.current[eventType] = true;
      setSession(response.data);
    },
    [session?.id],
  );

  const handleStartNarration = React.useCallback(async () => {
    const status = await startRecording(monitorStreamRef.current, selectedDeviceId);
    if (status === 'denied') {
      toast({
        title: 'Microphone access denied',
        description: 'Allow microphone access in your browser to record narration.',
        variant: 'destructive',
      });
      return;
    }
    if (status === 'error') {
      toast({
        title: 'Microphone unavailable',
        description: 'Studio could not start microphone recording.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await markTimingEvent('narration_start');
    } catch (error) {
      toast({
        title: 'Timing sync unavailable',
        description: error instanceof Error ? error.message : 'Failed to mark narration start timing.',
        variant: 'destructive',
      });
      return;
    }

  }, [markTimingEvent, monitorStreamRef, selectedDeviceId, startRecording, toast]);

  const handleStop = React.useCallback(async () => {
    if (!session?.id) return;

    setStopping(true);
    setStopRequested(true);
    setStopRequestedAtMs(Date.now());
    try {
      let narrationBlob: Blob | null = null;
      if (micStatus === 'active') {
        narrationBlob = await stopRecording();
      }

      // Upload narration audio BEFORE stopping so that raw_audio_path is set
      // on the DemoRecordingSession row before _finalize_stopped_session runs.
      if (narrationBlob && narrationBlob.size > 0) {
        try {
          const file = new File([narrationBlob], 'narration.webm', {
            type: narrationBlob.type || 'audio/webm',
          });
          const uploadResponse = await demoStudioService.uploadAudio(session.id, file);
          if (!uploadResponse.success || !uploadResponse.data) {
            throw new Error(uploadResponse.error || 'Failed to upload narration.');
          }
        } catch (uploadError) {
          toast({
            title: 'Narration upload failed',
            description: uploadError instanceof Error
              ? uploadError.message
              : 'Failed to upload narration.',
            variant: 'destructive',
          });
          // Proceed with stop anyway – the audio is already in storage even if
          // the DB update failed, or the user may want to stop regardless.
        }
      }

      const stopResponse = await demoStudioService.stopSession(session.id);

      if (!stopResponse.success || !stopResponse.data) {
        throw new Error(stopResponse.error || 'Failed to stop Studio session.');
      }

      toast({
        title: 'Session stopped',
        description: 'The recording is being finalized in the background.',
      });
      navigate('/demo-studio', { replace: true });
    } catch (error) {
      setStopRequested(false);
      setStopRequestedAtMs(null);
      toast({
        title: 'Stop failed',
        description: error instanceof Error ? error.message : 'Failed to stop Studio session.',
        variant: 'destructive',
      });
    } finally {
      setStopping(false);
    }
  }, [micStatus, navigate, session?.id, stopRecording, toast]);

  const isActive = Boolean(session && ACTIVE_STATUSES.has(session.status));
  const liveViewUrl = session?.fullscreen_live_view_url || session?.live_view_url || null;
  const usingFullscreenLiveView = Boolean(session?.fullscreen_live_view_url);
  const liveViewRatio = usingFullscreenLiveView ? FULLSCREEN_LIVE_VIEW_RATIO : HEADFUL_LIVE_VIEW_RATIO;
  const liveViewZoom = usingFullscreenLiveView ? FULLSCREEN_LIVE_VIEW_ZOOM : 1;
  const controlsDisabled = loading || stopping || !isActive;
  const showFinishingState = stopRequested;
  return (
    <>
      <Helmet>
        <title>Studio Session | Hobbes</title>
      </Helmet>

      <div className="h-screen overflow-hidden bg-[#191919] text-white">
        <div className="flex h-screen flex-col">
          <main className="flex flex-1 flex-col overflow-hidden px-3 pb-3 pt-3">
            <div className="flex flex-1 min-h-0">
              <section className="flex min-w-0 flex-1 flex-col items-center justify-center">
                <div
                  className="flex flex-col overflow-hidden rounded-2xl border border-[#2b2b2b] bg-[#101010] shadow-[0_8px_20px_rgba(0,0,0,0.05)]"
                  style={{
                    width: `min(calc((100vh - 132px) * ${liveViewRatio}), calc(100vw - 32px))`,
                  }}
                >
                  <div className="relative w-full overflow-hidden bg-[#0f0f0f]">
                    <AspectRatio ratio={liveViewRatio} className="w-full bg-[#0f0f0f]">
                      {loading ? (
                        <div className="flex h-full w-full items-center justify-center bg-[#111111]">
                          <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm text-white/80">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading workspace...
                          </div>
                        </div>
                      ) : showFinishingState ? (
                        <div className="flex h-full w-full items-center justify-center bg-[#101010] p-6">
                          <Card className="w-full max-w-lg border-white/10 bg-white/5 text-white shadow-none">
                            <CardHeader>
                              <CardTitle className="text-white">
                                Finishing recording
                              </CardTitle>
                              <CardDescription className="text-white/70">
                                We are closing the managed browser and returning you to Studio.
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="flex items-center gap-3">
                              <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm text-white/80">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Stopping recording...
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      ) : session?.status === 'abandoned' ? (
                        <div className="flex h-full w-full items-center justify-center bg-[#101010] p-6">
                          <Card className="w-full max-w-lg border-white/10 bg-white/5 text-white shadow-none">
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2 text-white">
                                <Clock className="h-5 w-5 text-zinc-400" />
                                Session expired
                              </CardTitle>
                              <CardDescription className="text-white/70">
                                The managed browser was closed due to inactivity. Return to Studio to start a new recording.
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              <Button onClick={() => navigate('/demo-studio')}>Back to Studio</Button>
                            </CardContent>
                          </Card>
                        </div>
                      ) : liveViewUrl && isActive ? (
                        <div className="h-full w-full overflow-hidden bg-[#0f0f0f]">
                          <iframe
                            src={liveViewUrl}
                            title="Studio Live View"
                            className="block h-full w-full origin-center border-0 bg-[#0f0f0f]"
                            style={{
                              transform: `scale(${liveViewZoom})`,
                              width: `${100 / liveViewZoom}%`,
                              height: `${100 / liveViewZoom}%`,
                              marginLeft: `${((100 / liveViewZoom) - 100) / -2}%`,
                              marginTop: `${((100 / liveViewZoom) - 100) / -2}%`,
                            }}
                            allow="clipboard-read; clipboard-write"
                            onLoad={() => {
                              void markTimingEvent('visible').catch(() => undefined);
                            }}
                          />
                        </div>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center bg-[#101010] p-6">
                          <Card className="w-full max-w-lg border-white/10 bg-white/5 text-white shadow-none">
                            <CardHeader>
                              <CardTitle className="text-white">Session unavailable</CardTitle>
                              <CardDescription className="text-white/70">
                                The live workspace is no longer active. Return to Studio to launch a new session.
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              <Button onClick={() => navigate('/demo-studio')}>Back to Studio</Button>
                            </CardContent>
                          </Card>
                        </div>
                      )}
                    </AspectRatio>
                  </div>
                </div>
              </section>
            </div>

            <div className="flex items-center justify-center px-4 pb-1 pt-3">
              <div className="flex h-11 items-center rounded-full border border-white/[0.08] bg-white/[0.03] text-[13px] font-medium tracking-[-0.1px] text-white/90 backdrop-blur-sm">
                {/* Timer */}
                <div className="flex items-center gap-2 pl-4 pr-3.5">
                  <span
                    className={`h-2 w-2 rounded-full ${
                      session?.narration_start_ms != null ? 'bg-[#c03740] shadow-[0_0_6px_rgba(192,55,64,0.5)]' : 'bg-white/20'
                    }`}
                  />
                  <span className="tabular-nums text-white/70">{formatDuration(elapsedSeconds)}</span>
                </div>

                <div className="h-4 w-px bg-white/[0.08]" />

                {/* Mic mute toggle */}
                <button
                  type="button"
                  onClick={toggleMute}
                  className={`flex items-center justify-center px-3 transition-colors ${
                    muted ? 'text-red-400/90 hover:text-red-300' : 'text-white/60 hover:text-white/90'
                  }`}
                  aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
                >
                  {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </button>

                {/* Audio level bars — always live when mic connected */}
                {micConnected && (
                  <div className="flex items-center pr-1">
                    <AudioLevelBars audioLevel={muted ? 0 : audioLevel} />
                  </div>
                )}

                {/* Device selector (only when multiple mics) */}
                {inputDevices.length > 1 && (
                  <>
                    <div className="h-4 w-px bg-white/[0.08]" />
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          disabled={controlsDisabled || micStatus === 'active'}
                          className="flex items-center gap-1.5 px-3 text-white/50 transition-colors hover:text-white/80 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <span className="max-w-[120px] truncate text-[12px]">
                            {inputDevices.find((d) => d.deviceId === selectedDeviceId)?.label ??
                              inputDevices[0]?.label ?? 'Mic'}
                          </span>
                          <ChevronUp className="h-3 w-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="top"
                        align="center"
                        sideOffset={8}
                        className="w-72 border-white/[0.08] bg-[#1a1a1a] p-1 shadow-xl"
                      >
                        {inputDevices.map((device) => (
                          <button
                            key={device.deviceId}
                            type="button"
                            onClick={() => setSelectedDeviceId(device.deviceId)}
                            className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-[13px] text-white/80 transition hover:bg-white/[0.06] ${
                              (selectedDeviceId === device.deviceId ||
                                (!selectedDeviceId && device.deviceId === inputDevices[0]?.deviceId))
                                ? 'bg-white/[0.04] text-white'
                                : ''
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                                (selectedDeviceId === device.deviceId ||
                                  (!selectedDeviceId && device.deviceId === inputDevices[0]?.deviceId))
                                  ? 'bg-[#60A5FA]'
                                  : 'bg-transparent'
                              }`}
                            />
                            <span className="truncate">{device.label}</span>
                          </button>
                        ))}
                      </PopoverContent>
                    </Popover>
                  </>
                )}

                <div className="h-4 w-px bg-white/[0.08]" />

                {/* Record / Stop */}
                <button
                  type="button"
                  onClick={micStatus === 'active' ? handleStop : () => void handleStartNarration()}
                  disabled={controlsDisabled}
                  className={`flex items-center gap-2 rounded-r-full pl-3.5 pr-4 transition disabled:cursor-not-allowed disabled:opacity-40 ${
                    micStatus === 'active'
                      ? 'bg-[#c03740]/20 text-[#f87171] hover:bg-[#c03740]/30'
                      : 'text-white/70 hover:bg-white/[0.04] hover:text-white'
                  }`}
                  style={{ height: '100%' }}
                  aria-label={micStatus === 'active' ? 'Stop recording' : 'Start recording'}
                >
                  {stopping ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Stopping</span>
                    </>
                  ) : micStatus === 'active' ? (
                    <>
                      <Square className="h-3 w-3 fill-current" />
                      <span>Stop</span>
                    </>
                  ) : (
                    <>
                      <Circle className="h-3.5 w-3.5 fill-[#c03740] text-[#c03740]" />
                      <span>Record</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default DemoStudioSessionPage;
